import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import {
  Phone,
  X,
  Clock,
  Check,
  Loader2,
  PhoneIncoming,
  PhoneOutgoing,
  Voicemail,
  Calendar,
  Mic,
  AlertCircle,
  CheckCircle2,
  ArrowRightLeft,
  User,
} from 'lucide-react';
import type { Contact } from '@/types/contacts';

interface CallLoggerProps {
  contact: Contact;
  onClose: () => void;
  onLogged: () => void;
}

type CallDirection = 'inbound' | 'outbound';
type CallOutcome = 'completed' | 'no_answer' | 'voicemail' | 'busy' | 'scheduled' | 'callback_requested';

interface CallFormData {
  direction: CallDirection;
  phoneNumber: string;
  duration: number;
  durationMinutes: number;
  durationSeconds: number;
  outcome: CallOutcome;
  notes: string;
  isScheduled: boolean;
  scheduledDate: string;
  scheduledTime: string;
}

const outcomeOptions: { value: CallOutcome; label: string; icon: React.ReactNode; color: string }[] = [
  { value: 'completed', label: 'Completed', icon: <CheckCircle2 className="h-4 w-4" />, color: 'bg-green-500/10 text-green-600' },
  { value: 'no_answer', label: 'No Answer', icon: <AlertCircle className="h-4 w-4" />, color: 'bg-red-500/10 text-red-600' },
  { value: 'voicemail', label: 'Voicemail', icon: <Voicemail className="h-4 w-4" />, color: 'bg-amber-500/10 text-amber-600' },
  { value: 'busy', label: 'Busy', icon: <AlertCircle className="h-4 w-4" />, color: 'bg-orange-500/10 text-orange-600' },
  { value: 'scheduled', label: 'Scheduled Follow-up', icon: <Calendar className="h-4 w-4" />, color: 'bg-blue-500/10 text-blue-600' },
  { value: 'callback_requested', label: 'Callback Requested', icon: <PhoneIncoming className="h-4 w-4" />, color: 'bg-purple-500/10 text-purple-600' },
];

export function CallLogger({ contact, onClose, onLogged }: CallLoggerProps) {
  const [formData, setFormData] = useState<CallFormData>({
    direction: 'outbound',
    phoneNumber: contact.phone || '',
    duration: 0,
    durationMinutes: 0,
    durationSeconds: 0,
    outcome: 'completed',
    notes: '',
    isScheduled: false,
    scheduledDate: new Date().toISOString().split('T')[0],
    scheduledTime: '10:00',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [timerActive, setTimerActive] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(0);

  // Timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (timerActive) {
      interval = setInterval(() => {
        setTimerSeconds((prev) => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timerActive]);

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSubmit = async () => {
    if (!formData.phoneNumber.trim()) return;

    setIsSubmitting(true);
    try {
      const duration = formData.isScheduled
        ? 0
        : formData.durationMinutes * 60 + formData.durationSeconds || timerSeconds;

      const scheduledAt = formData.isScheduled
        ? new Date(`${formData.scheduledDate}T${formData.schedTime}`).toISOString()
        : undefined;

      const response = await fetch('/api/crm/communications/calls', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contactId: contact.id,
          direction: formData.direction,
          phoneNumber: formData.phoneNumber,
          duration,
          outcome: formData.isScheduled ? 'scheduled' : formData.outcome,
          notes: formData.notes,
          scheduledAt,
        }),
      });

      if (response.ok) {
        onLogged();
      }
    } catch (error) {
      console.error('Failed to log call:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const quickDurations = [
    { label: '5 min', value: 300 },
    { label: '10 min', value: 600 },
    { label: '15 min', value: 900 },
    { label: '30 min', value: 1800 },
  ];

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Phone className="h-5 w-5 text-green-600" />
            Log Call
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Contact Info Card */}
          <Card className="bg-muted/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-[#2B7FFF]/10 flex items-center justify-center">
                  <User className="h-5 w-5 text-[#2B7FFF]" />
                </div>
                <div>
                  <div className="font-medium">{contact.name}</div>
                  <div className="text-sm text-muted-foreground">
                    {contact.company || contact.title || contact.email}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Direction Toggle */}
          <div className="space-y-2">
            <Label>Call Direction</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setFormData((prev) => ({ ...prev, direction: 'outbound' }))}
                className={cn(
                  'flex items-center justify-center gap-2 p-3 rounded-lg border transition-all',
                  formData.direction === 'outbound'
                    ? 'border-[#2B7FFF] bg-[#2B7FFF]/5 text-[#2B7FFF]'
                    : 'border-border hover:bg-muted'
                )}
              >
                <PhoneOutgoing className="h-4 w-4" />
                Outbound
              </button>
              <button
                onClick={() => setFormData((prev) => ({ ...prev, direction: 'inbound' }))}
                className={cn(
                  'flex items-center justify-center gap-2 p-3 rounded-lg border transition-all',
                  formData.direction === 'inbound'
                    ? 'border-green-500 bg-green-500/5 text-green-600'
                    : 'border-border hover:bg-muted'
                )}
              >
                <PhoneIncoming className="h-4 w-4" />
                Inbound
              </button>
            </div>
          </div>

          {/* Phone Number */}
          <div className="space-y-2">
            <Label htmlFor="phone">Phone Number</Label>
            <Input
              id="phone"
              value={formData.phoneNumber}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, phoneNumber: e.target.value }))
              }
              placeholder="+1 (555) 123-4567"
            />
          </div>

          {/* Scheduled Toggle */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Switch
                id="scheduled"
                checked={formData.isScheduled}
                onCheckedChange={(checked) =>
                  setFormData((prev) => ({ ...prev, isScheduled: checked }))
                }
              />
              <Label htmlFor="scheduled" className="cursor-pointer">
                Schedule Future Call
              </Label>
            </div>
            {formData.isScheduled && <Calendar className="h-4 w-4 text-blue-500" />}
          </div>

          {/* Scheduled Date/Time */}
          {formData.isScheduled && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="grid grid-cols-2 gap-3"
            >
              <div className="space-y-2">
                <Label>Date</Label>
                <Input
                  type="date"
                  value={formData.scheduledDate}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, scheduledDate: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Time</Label>
                <Input
                  type="time"
                  value={formData.scheduledTime}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, scheduledTime: e.target.value }))
                  }
                />
              </div>
            </motion.div>
          )}

          {/* Duration - Only show if not scheduled */}
          {!formData.isScheduled && (
            <div className="space-y-3">
              <Label>Duration</Label>
              
              {/* Timer Display */}
              <div className="flex items-center justify-center p-4 bg-muted rounded-lg">
                <div className="text-3xl font-mono font-semibold">
                  {formatDuration(timerActive ? timerSeconds : formData.duration)}
                </div>
              </div>

              {/* Timer Controls */}
              <div className="flex justify-center gap-2">
                <Button
                  variant={timerActive ? 'destructive' : 'default'}
                  size="sm"
                  onClick={() => {
                    if (timerActive) {
                      setTimerActive(false);
                      setFormData((prev) => ({ ...prev, duration: timerSeconds }));
                    } else {
                      setTimerActive(true);
                    }
                  }}
                >
                  {timerActive ? (
                    <>
                      <Clock className="h-4 w-4 mr-2" />
                      Stop Timer
                    </>
                  ) : (
                    <>
                      <Clock className="h-4 w-4 mr-2" />
                      Start Timer
                    </>
                  )}
                </Button>
                {timerSeconds > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setTimerSeconds(0);
                      setTimerActive(false);
                    }}
                  >
                    Reset
                  </Button>
                )}
              </div>

              {/* Quick Duration Buttons */}
              <div className="flex flex-wrap gap-2">
                {quickDurations.map((d) => (
                  <button
                    key={d.value}
                    onClick={() => {
                      setTimerActive(false);
                      setTimerSeconds(d.value);
                      setFormData((prev) => ({ ...prev, duration: d.value }));
                    }}
                    className={cn(
                      'px-3 py-1.5 rounded-md text-sm border transition-colors',
                      formData.duration === d.value && !timerActive
                        ? 'bg-[#2B7FFF] text-white border-[#2B7FFF]'
                        : 'border-border hover:bg-muted'
                    )}
                  >
                    {d.label}
                  </button>
                ))}
              </div>

              {/* Manual Duration Input */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs">Minutes</Label>
                  <Input
                    type="number"
                    min={0}
                    value={formData.durationMinutes}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        durationMinutes: parseInt(e.target.value) || 0,
                      }))
                    }
                    onFocus={() => setTimerActive(false)}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Seconds</Label>
                  <Input
                    type="number"
                    min={0}
                    max={59}
                    value={formData.durationSeconds}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        durationSeconds: parseInt(e.target.value) || 0,
                      }))
                    }
                    onFocus={() => setTimerActive(false)}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Outcome */}
          {!formData.isScheduled && (
            <div className="space-y-2">
              <Label>Outcome</Label>
              <div className="grid grid-cols-2 gap-2">
                {outcomeOptions.map((outcome) => (
                  <button
                    key={outcome.value}
                    onClick={() =>
                      setFormData((prev) => ({ ...prev, outcome: outcome.value }))
                    }
                    className={cn(
                      'flex items-center gap-2 p-2.5 rounded-lg border text-left transition-all',
                      formData.outcome === outcome.value
                        ? 'border-[#2B7FFF] ring-1 ring-[#2B7FFF]'
                        : 'border-border hover:bg-muted'
                    )}
                  >
                    <div className={cn('p-1.5 rounded', outcome.color)}>
                      {outcome.icon}
                    </div>
                    <span className="text-sm">{outcome.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Call Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, notes: e.target.value }))
              }
              placeholder="What was discussed? Any action items?"
              className="min-h-[100px]"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !formData.phoneNumber.trim()}
            className={cn(
              'min-w-[120px]',
              formData.isScheduled
                ? 'bg-blue-600 hover:bg-blue-700'
                : 'bg-green-600 hover:bg-green-700'
            )}
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Check className="h-4 w-4 mr-2" />
            )}
            {formData.isScheduled ? 'Schedule Call' : 'Log Call'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default CallLogger;
